/**
 * CountryFlag — render a real flag image for an ISO2 country code.
 *
 * Replaces the prior text-emoji approach (regional-indicator code points)
 * which fell back to the literal
 * "SV" / "AF" text on Windows browsers without emoji-flag support — which
 * (a) looked broken and (b) introduced a font-baseline alignment bug
 * vs the dial-code text that was effectively unfixable in pure CSS.
 *
 * Image bounding box has fixed pixel dimensions (no font metrics), so
 * `alignItems: 'center'` on the parent row centers the flag against the
 * dial code perfectly without per-glyph offset tuning.
 *
 * Image source: flagcdn.com — free CDN that serves SVG/PNG/WebP for
 * every ISO2 code. We pull the `w80` PNG (80px wide) which renders
 * crisp at our display size (22-32px logical width) on retina screens
 * and is a few KB per flag. The browser HTTP cache + service worker
 * cover repeat loads.
 *
 * Source: https://flagpedia.net/download/api
 *
 * NOTE on Image source: uses react-native's plain `Image` instead of
 * `expo-image`. expo-image worked inside the picker modal but failed
 * to render in the PhoneInput trigger pill on web (silent — image
 * element mounted but nothing visible). RN's `Image` renders a vanilla
 * `<img>` on web and works in every context. We don't lose much:
 * expo-image's caching layer is mostly redundant with the browser HTTP
 * cache for these tiny (~500 byte) PNGs.
 */
import { Image, View } from 'react-native'

interface CountryFlagProps {
  /** ISO 3166-1 alpha-2 country code (e.g. 'SV', 'US'). Case-insensitive. */
  iso2: string
  /**
   * Visual width in logical pixels. Height is auto-derived to keep the
   * 3:2 aspect ratio that flagcdn ships at. Default 22 fits a standard
   * input chip; bump to 28+ for picker rows.
   */
  width?: number
  /** Subtle rounding so the flag reads as a chip rather than a sticker. */
  borderRadius?: number
  testID?: string
}

export function CountryFlag({ iso2, width = 22, borderRadius = 2, testID }: CountryFlagProps) {
  const height = Math.round(width * 0.66) // 3:2 aspect — matches flagcdn
  // Light placeholder bg so the box is visible even before the network
  // round-trip finishes / if the request fails (no perceived "missing
  // flag" gap in the layout).
  const placeholderStyle = {
    width,
    height,
    borderRadius,
    backgroundColor: 'rgba(0,0,0,0.05)',
  }
  if (!iso2 || iso2.length !== 2) {
    return <View style={placeholderStyle} testID={testID} />
  }
  const code = iso2.toLowerCase()
  return (
    <Image
      source={{ uri: `https://flagcdn.com/w80/${code}.png` }}
      style={placeholderStyle}
      resizeMode="cover"
      accessibilityLabel={iso2.toUpperCase()}
      testID={testID}
    />
  )
}
